import { SiderProps } from 'antd/lib/layout';
import React, { FC } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Layout } from 'components/atoms/layout';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { useIsExternalUser } from 'hooks/use-user-type';
import { isChildNode } from 'utils/is-child-node';

const Sidebar = styled(Layout.Sider).attrs({
  collapsedWidth: 0,
  collapsible: true,
  defaultCollapsed: true,
  theme: 'light',
  width: 256,
  trigger: null,
})<{ isExternalUser: boolean }>`
  box-shadow: var(--shadow-16);
  z-index: var(--zindex-layer-09);
  position: fixed;
  top: 0;
  height: 100vh;
  left: ${({ isExternalUser }) =>
    isExternalUser ? '256px' : 'var(--side-nav-width)'};
`;

const SideBarWrapper = styled.div`
  &:before {
    content: '';
    position: fixed;
    background: rgba(0, 0, 0, 0);
    left: var(--side-nav-width);
    bottom: 0;
    top: 0;
    right: 0;
    z-index: var(--zindex-layer-09);
    pointer-events: none;
    transition: background-color ease-in-out var(--animation-duration-medium);
  }
  &:hover:before {
    left: var(--side-nav-width);
    background: rgba(0, 0, 0, 0.1);
  }
`;

interface Props
  extends Omit<
    SiderProps,
    | 'collapsedWidth'
    | 'collapsible'
    | 'defaultCollapsed'
    | 'theme'
    | 'width'
    | 'trigger'
  > {
  onClose: () => void;
}

export const Sider: FC<Props> = ({ children, collapsed, onClose, ...rest }) => {
  const isChild = isChildNode([
    'filter-box',
    'ant-dropdown',
    'ant-picker-dropdown',
    'ant-select-dropdown',
    'ant-popover-inner-content',
    'ant-modal-root',
  ]);

  const isExternalUser = useIsExternalUser();

  return ReactDOM.createPortal(
    <OutsideClickHandler allowed={isChild} onOutsideClick={onClose}>
      <SideBarWrapper>
        <Sidebar
          collapsed={collapsed}
          {...rest}
          isExternalUser={isExternalUser}
          data-cy="views.sider"
        >
          {!collapsed ? children : null}
        </Sidebar>
      </SideBarWrapper>
    </OutsideClickHandler>,
    document.body,
  );
};
